import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatFinnishCurrency } from '@/utils';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';
import {
  useGetSettlementRunsQuery,
  useRunSettlementMutation,
} from '@/services/SettlementsService';
import {
  SettlementRunItem,
  SettlementRunsListParams,
} from '@/types/settlements.types';
import {
  SettlementPeriodType as SettlementPeriodTypeEnum,
  SettlementRunStatus,
} from '@/constant';

const SETTLEMENT_STATUS_LABELS: Record<string, string> = {
  [SettlementRunStatus.DRAFT]: 'Draft',
  [SettlementRunStatus.FINALIZED]: 'Finalized',
};

const Settlements = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // List filters
  const [dateRange, setDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);
  const [statusFilter, setStatusFilter] = useState<SettlementRunStatus | 'all'>(
    'all'
  );

  // Run settlement controls
  const [runPeriodType, setRunPeriodType] = useState<SettlementPeriodTypeEnum>(
    SettlementPeriodTypeEnum.LAST_MONTH
  );
  const [runDateRange, setRunDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);
  const [isRunConfirmOpen, setIsRunConfirmOpen] = useState(false);

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const listStart = dateRange[0]
    ? moment(dateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;
  const listEnd = dateRange[1]
    ? moment(dateRange[1], 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;

  const listParams: SettlementRunsListParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    period_start_from: listStart,
    period_end_to: listEnd,
  };

  const {
    data: list,
    isFetching: listLoading,
    error: listError,
  } = useGetSettlementRunsQuery(listParams);

  const [runSettlement, { isLoading: isRunning }] = useRunSettlementMutation();

  const rows: SettlementRunItem[] = useMemo(() => list?.items ?? [], [list]);

  useEffect(() => {
    if (list) {
      handleTotalPages(list.totalPages || 1);
      handleTotalItems(list.totalItems || 0);
    }
  }, [list, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    if (!listError) return;
    const err = listError as unknown as {
      data?: unknown;
      error?: string;
    } | null;
    let message = 'Failed to fetch settlement runs';
    if (
      err?.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err?.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [listError, enqueueSnackbar]);

  const columns = [
    {
      accessorFn: (row: SettlementRunItem) => row.period_start,
      id: 'period',
      header: 'Period',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => {
        const start = row.original.period_start
          ? formatDisplayDate(row.original.period_start, '')
          : '';
        const end = row.original.period_end
          ? formatDisplayDate(row.original.period_end, '')
          : '';
        return (
          <span>
            {start} - {end}
          </span>
        );
      },
    },
    {
      accessorFn: (row: SettlementRunItem) => row.status,
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => (
        <span
          className={`badge ${row.original.status === 'FINALIZED'
            ? 'badge-success'
            : row.original.status === 'FAILED'
              ? 'badge-danger'
              : 'badge-warning'
            }`}
        >
          {SETTLEMENT_STATUS_LABELS[row.original.status] || row.original.status}
        </span>
      ),
    },
    {
      accessorFn: (row: SettlementRunItem) => row.actor_count,
      id: 'actors',
      header: 'Actors',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => (
        <span>{row.original.actor_count}</span>
      ),
    },
    {
      accessorFn: (row: SettlementRunItem) => row.total_net_payable,
      id: 'net_payable',
      header: 'Total Net Payable',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => (
        <span>{formatFinnishCurrency(row.original.total_net_payable)}</span>
      ),
    },
    {
      accessorFn: (row: SettlementRunItem) =>
        row.config_snapshot?.min_payout_threshold_eur ?? null,
      id: 'threshold',
      header: 'Min Payout Threshold',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => {
        const threshold =
          row.original.config_snapshot?.min_payout_threshold_eur ?? null;
        return threshold !== null && threshold !== undefined ? (
          <span>{formatFinnishCurrency(threshold)}</span>
        ) : (
          <span className="text-gray-400"></span>
        );
      },
    },
    {
      accessorFn: (row: SettlementRunItem) => row.created_at,
      id: 'created_at',
      header: 'Created At',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => (
        <span className="text-sm">
          {row.original.created_at
            ? formatDisplayDateTime(row.original.created_at)
            : ''}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: { row: { original: SettlementRunItem } }) => (
        <button
          onClick={() => navigate(`/settlements/${row.original.id}`)}
          className="btn btn-sm btn-icon btn-light btn-outline"
          title="View settlement details"
        >
          <KeenIcon icon="eye" className="text-lg" />
        </button>
      ),
    },
  ];

  const handleRunSettlement = async () => {
    try {
      let period_start: string;
      let period_end: string;
      let period_type: string;

      if (runPeriodType === SettlementPeriodTypeEnum.LAST_MONTH) {
        const nowUtc = moment.utc();
        const start = nowUtc.clone().subtract(1, 'month').startOf('month');
        const end = nowUtc.clone().subtract(1, 'month').endOf('month');
        // Send UTC ISO timestamps aligned to calendar month boundaries
        period_start = start.toISOString();
        period_end = end.toISOString();
        period_type = SettlementPeriodTypeEnum.LAST_MONTH;
      } else {
        if (!runDateRange[0] || !runDateRange[1]) {
          enqueueSnackbar('Please select a start and end date', {
            variant: 'warning',
          });
          return;
        }
        const start = moment.utc(runDateRange[0], 'DD/MM/YYYY').startOf('day');
        const end = moment.utc(runDateRange[1], 'DD/MM/YYYY').endOf('day');
        // Use the selected calendar days as UTC ISO timestamps (no local TZ shift)
        period_start = start.toISOString();
        period_end = end.toISOString();
        period_type = SettlementPeriodTypeEnum.CUSTOM;
      }

      await runSettlement({ period_type, period_start, period_end }).unwrap();
      enqueueSnackbar('Settlement run triggered successfully', {
        variant: 'success',
      });
    } catch (e) {
      let message = 'Failed to run settlement';
      if (
        e &&
        typeof e === 'object' &&
        'data' in (e as Record<string, unknown>)
      ) {
        const data = (e as { data?: { message?: string } }).data;
        if (data?.message) message = data.message;
      } else if (
        e &&
        typeof e === 'object' &&
        'error' in (e as { error?: string })
      ) {
        const errStr = (e as { error?: string }).error;
        if (errStr) message = errStr;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Container>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h1
            id="settlements-heading"
            className="text-xl font-semibold"
            tabIndex={-1}
          >
            Settlements
          </h1>
        </div>

        {/* Run settlement section */}
        <div className="card">
          <div className="card-body flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Run Settlement</h2>
                <p className="text-sm text-gray-500">
                  Trigger a settlement run for a given period. All paid and not
                  yet settled transactions in the period will be grouped per
                  financial actor.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label
                  htmlFor="settlement-period-type"
                  className="text-sm font-medium"
                >
                  Period Type:
                </label>
                <select
                  id="settlement-period-type"
                  className="select select-sm w-40"
                  value={runPeriodType}
                  onChange={(e) =>
                    setRunPeriodType(e.target.value as SettlementPeriodTypeEnum)
                  }
                >
                  <option value={SettlementPeriodTypeEnum.LAST_MONTH}>
                    Last Month
                  </option>
                  <option value={SettlementPeriodTypeEnum.CUSTOM}>
                    Custom
                  </option>
                </select>
              </div>

              {runPeriodType === SettlementPeriodTypeEnum.CUSTOM && (
                <div>
                  <DateRangePicker
                    startDateName="run_start_date"
                    endDateName="run_end_date"
                    isMandatory={false}
                    label={undefined}
                    value={runDateRange}
                    onChange={(dates) => setRunDateRange(dates)}
                    allowPastDates={true}
                    allowFutureDates={false}
                    className="w-full sm:w-56 md:w-64 h-8 sm:h-10 text-sm"
                  />
                </div>
              )}

              <button
                className="btn btn-sm btn-primary flex items-center gap-2 h-8 sm:h-10 text-white"
                onClick={() => setIsRunConfirmOpen(true)}
                disabled={isRunning}
              >
                {isRunning ? 'Running...' : 'Run Settlement'}
              </button>
            </div>
          </div>
        </div>

        {/* List section */}
        <div className="card">
          <div className="card-body">
            <div className="card-header px-0 pt-0 pb-4 flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                {/* Status filter */}
                <div className="flex-shrink-0">
                  <select
                    className="select select-sm w-32 h-8 sm:h-10 text-sm"
                    style={{ minWidth: 'max-content' }}
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(
                        e.target.value === 'all'
                          ? 'all'
                          : (e.target.value as SettlementRunStatus)
                      )
                    }
                    aria-label="Filter by settlement status"
                  >
                    <option value="all">All Status</option>
                    <option value={SettlementRunStatus.DRAFT}>
                      {SETTLEMENT_STATUS_LABELS[SettlementRunStatus.DRAFT]}
                    </option>
                    <option value={SettlementRunStatus.FINALIZED}>
                      {SETTLEMENT_STATUS_LABELS[SettlementRunStatus.FINALIZED]}
                    </option>
                  </select>
                </div>

                {/* Date Range filter */}
                <div className="flex-shrink-0">
                  <DateRangePicker
                    startDateName="period_start_from"
                    endDateName="period_end_to"
                    isMandatory={false}
                    label={undefined}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates)}
                    allowPastDates={true}
                    allowFutureDates={false}
                    className="w-full sm:w-56 md:w-64 h-8 sm:h-10 text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {(!!dateRange[0] ||
                  !!dateRange[1] ||
                  statusFilter !== 'all') && (
                    <button
                      className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                      onClick={() => {
                        setDateRange([undefined, undefined]);
                        setStatusFilter('all');
                        setPagination((p) => ({ ...p, pageIndex: 0 }));
                      }}
                      type="button"
                      aria-label="Clear settlement filters"
                    >
                      <KeenIcon
                        type="solid"
                        icon="arrows-circle"
                        className="text-gray-600"
                      />
                      Clear Filters
                    </button>
                  )}
              </div>
            </div>

            <DataGrid
              columns={columns}
              data={rows}
              rowSelect={false}
              loading={listLoading}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: '{from} - {to} of {count}',
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              serverSide={true}
              hasActiveFilters={
                !!dateRange[0] || !!dateRange[1] || statusFilter !== 'all'
              }
              onFilterReset={() => {
                setDateRange([undefined, undefined]);
                setStatusFilter('all');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              aria-label="Settlement runs table"
            />
          </div>
        </div>
      </div>
      <ConfirmationDialog
        open={isRunConfirmOpen}
        onClose={() => setIsRunConfirmOpen(false)}
        onConfirm={() => {
          setIsRunConfirmOpen(false);
          void handleRunSettlement();
        }}
        message={
          <div className="space-y-2">
            <p>
              Are you sure you want to run settlements for the selected period?
            </p>
            <p className="text-sm text-gray-600">
              This will create settlement runs for all paid, not yet settled
              transactions in that period, grouped per financial actor.
            </p>
          </div>
        }
        cancelButtonText="Cancel"
        confirmButtonText={isRunning ? 'Running...' : 'Run Settlement'}
        disabled={isRunning}
      />
    </Container>
  );
};

export default Settlements;
